import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { Analysis, AnalyzeRequest } from '@fishymeter/types';
import {
  useAnalysisStatus,
  useSubmitAnalysis,
  useSubmitFileAnalysis,
} from '@/hooks/use-analyze';
import { AnalysisResult } from '@/components/analysis-result';
import { FeedItemCard } from '@/components/feed-item-card';
import { FileDropzone, fileInputType } from '@/components/file-dropzone';
import { InputTabs } from '@/components/input-tabs';
import { ShareButton } from '@/components/share-button';
import { SignInGate } from '@/components/sign-in-gate';
import { api, ApiError, type FeedItem } from '@/lib/api';
import { useFeatureFlags } from '@/lib/feature-flags';
import { INPUT_TABS_ORDER, INPUT_TAB_LABEL, type InputTabId } from '@/lib/input-tabs-order';

type Mode = InputTabId;

const MODE_TO_FLAG: Record<Mode, 'analyzeFile' | 'analyzeUrl' | 'analyzeText'> = {
  file: 'analyzeFile',
  url: 'analyzeUrl',
  text: 'analyzeText',
};

const urlSchema = z
  .string()
  .trim()
  .url('Enter a valid URL')
  .refine((u) => /^https?:\/\//i.test(u), 'Must be http(s)');

function countWords(s: string): number {
  return s.trim().split(/\s+/).filter(Boolean).length;
}

export function HomePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  // Initial mode = first enabled tab in display order. If everything is
  // off, still pick the first tab so the form has something to render —
  // submit will be blocked by the comingSoon gate anyway.
  const initialMode =
    INPUT_TABS_ORDER.find((id) => flags[MODE_TO_FLAG[id]]) ?? INPUT_TABS_ORDER[0];
  const [mode, setMode] = useState<Mode>(initialMode);
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [dailyLimit, setDailyLimit] = useState<{ limit?: number } | null>(null);
  const submit = useSubmitAnalysis();
  const fileSubmit = useSubmitFileAnalysis();
  const status = useAnalysisStatus(analysisId);


  const data = status.data;
  const isComplete = data?.status === 'complete';
  const isFailed = data?.status === 'failed';
  const isProcessing =
    (!!analysisId && !isComplete && !isFailed) || fileSubmit.phase !== 'idle';

  const isPending = submit.isPending || fileSubmit.isPending;

  const handleApiError = (err: unknown) => {
    if (err instanceof ApiError) {
      if (err.code === 'USERNAME_REQUIRED') {
        navigate('/welcome');
        return;
      }
      if (err.code === 'RATE_LIMIT_EXCEEDED' && err.body?.reason === 'daily_limit') {
        setDailyLimit({ limit: err.body?.limit });
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isProcessing || isPending) return;

    setAnalysisId(null);
    submit.reset();
    fileSubmit.reset();

    if (mode === 'file') {
      if (!file) return;
      // input_type is a property of the file, not of the tab. The dropzone
      // only accepts PNG, JPEG and PDF, so this derivation is total.
      fileSubmit.mutate(
        { kind: fileInputType(file), file },
        {
          onSuccess: ({ analysis_id }) => {
            setAnalysisId(analysis_id);
            setDailyLimit(null);
          },
          onError: handleApiError,
        },
      );
      return;
    }

    let request: AnalyzeRequest;
    if (mode === 'text') {
      const wc = countWords(text);
      if (wc === 0 || wc > 2000) return;
      request = { input_type: 'text', text };
    } else {
      const parsed = urlSchema.safeParse(url);
      if (!parsed.success) return;
      request = { input_type: 'url', url: parsed.data };
    }

    submit.mutate(request, {
      onSuccess: ({ analysis_id }) => {
        setAnalysisId(analysis_id);
        setDailyLimit(null);
      },
      onError: handleApiError,
    });
  };

  const handleReset = () => {
    setAnalysisId(null);
    setText('');
    setUrl('');
    setFile(null);
    setDailyLimit(null);
    submit.reset();
    fileSubmit.reset();
  };

  const modeEnabled = flags[MODE_TO_FLAG[mode]];
  const submitDisabled =
    isProcessing ||
    isPending ||
    !modeEnabled ||
    (mode === 'text' && (countWords(text) === 0 || countWords(text) > 2000)) ||
    (mode === 'url' && !urlSchema.safeParse(url).success) ||
    (mode === 'file' && !file);

  // Order matters: fileSubmit.phase is the more specific signal and must be
  // checked before isPending. isPending stays true for the whole file
  // mutation (all three steps), so testing it first made the 'Uploading…'
  // branch unreachable and the user never saw the upload phase.
  const submitLabel =
    fileSubmit.phase === 'uploading'
      ? 'Uploading…'
      : isPending
        ? 'Submitting…'
        : isProcessing
          ? 'Scoring…'
          : isFailed
            ? 'Try again'
            : 'Analyze';

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <header className="text-center">
        <h1 className="text-5xl font-bold tracking-tight">🐟 Fishymeter</h1>
        <p className="mt-3 text-lg text-muted-foreground">Everything's fishy.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Check any image, article, blog, or PDF for credibility before you trust it.
        </p>
      </header>

      {!auth.isAuthenticated && !auth.isLoading && <SignInGate />}

      {dailyLimit && (
        <section className="mt-10 rounded-lg border border-primary/40 bg-primary/5 p-6 text-center">
          <h2 className="text-lg font-semibold text-foreground">
            You've reached your daily limit
            {dailyLimit.limit ? ` of ${dailyLimit.limit} analyses` : ''}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Caps reset at midnight UTC. Try again tomorrow.
          </p>
        </section>
      )}

      {auth.isAuthenticated && (
        <>
          <form onSubmit={handleSubmit} className="mt-10">
            <InputTabs<Mode>
              value={mode}
              onChange={setMode}
              disabled={isProcessing || isPending}
              options={INPUT_TABS_ORDER.map((id) => ({
                value: id,
                label: INPUT_TAB_LABEL[id],
                comingSoon: !flags[MODE_TO_FLAG[id]],
              }))}
            />

            <div className="mt-4">
              {mode === 'url' && (
                <UrlField value={url} onChange={setUrl} disabled={isProcessing} />
              )}
              {mode === 'text' && (
                <TextField value={text} onChange={setText} disabled={isProcessing} />
              )}
              {mode === 'file' && (
                <FileDropzone
                  file={file}
                  onChange={setFile}
                  disabled={isProcessing || isPending}
                />
              )}
            </div>

            <div className="mt-4 flex gap-3">
              {isComplete ? (
                <button
                  type="button"
                  onClick={handleReset}
                  className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
                >
                  Analyze another
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={submitDisabled}
                  className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
                >
                  {submitLabel}
                </button>
              )}
            </div>
            {(submit.isError || fileSubmit.isError) && !dailyLimit && (
              <SubmitError error={submit.error ?? fileSubmit.error} />
            )}
          </form>

          {isProcessing && <ProcessingState />}
          {isFailed && data && 'error_code' in data && (
            <FailedState code={data.error_code} message={data.error_message} />
          )}
          {isComplete && data && analysisId && (
            <div className="mt-12">
              <AnalysisResult
                analysis={data as unknown as Analysis}
                actions={
                  <ShareButton
                    analysisId={analysisId}
                    initialIsPublic={(data as unknown as Analysis).is_public ?? false}
                  />
                }
              />
            </div>
          )}
        </>
      )}

      <FeedSections />

      <footer className="mt-16 border-t border-border pt-6 text-center text-xs text-muted-foreground">
        <p>
          AI scoring is advisory, not authoritative. Scores reflect the model's judgment at the
          time of analysis.
        </p>
      </footer>
    </div>
  );
}

function FeedSections() {
  const flags = useFeatureFlags();

  const recent = useQuery({
    queryKey: ['feed', 'recent'],
    queryFn: () => api.getRecentFeed(),
    staleTime: 60_000,
    enabled: flags.feedRecent,
  });
  const freshest = useQuery({
    queryKey: ['feed', 'freshest'],
    queryFn: () => api.getFreshestFeed(),
    staleTime: 60_000,
    enabled: flags.feedFreshest,
  });

  if (!flags.feedRecent && !flags.feedFreshest) return null;

  return (
    <section className="mt-16 space-y-12">
      {flags.feedRecent && (
        <FeedSection
          title="Recent Catches"
          subtitle="Latest public analyses"
          items={recent.data?.items}
          loading={recent.isLoading}
        />
      )}
      {flags.feedFreshest && (
        <FeedSection
          title="Freshest This Week"
          subtitle="Highest scored in the last 7 days"
          items={freshest.data?.items}
          loading={freshest.isLoading}
        />
      )}
    </section>
  );
}

function FeedSection({
  title,
  subtitle,
  items,
  loading,
}: {
  title: string;
  subtitle: string;
  items?: FeedItem[];
  loading: boolean;
}) {
  return (
    <div>
      <header>
        <h2 className="text-xl font-bold">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </header>
      {loading && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}
      {!loading && items && items.length === 0 && (
        <p className="mt-4 rounded-lg border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
          No public catches yet. Be the first.
        </p>
      )}
      {!loading && items && items.length > 0 && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {items.map((item) => (
            <FeedItemCard key={item.analysis_id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function UrlField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const [touched, setTouched] = useState(false);
  const validation = urlSchema.safeParse(value);
  const showError = touched && value.length > 0 && !validation.success;

  return (
    <div>
      <label htmlFor="url-input" className="block text-sm font-medium text-foreground">
        Paste a URL to analyze
      </label>
      <input
        id="url-input"
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        disabled={disabled}
        placeholder="https://example.com/article"
        className="mt-2 block w-full rounded-md border border-border bg-card p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
      />
      {showError && (
        <p className="mt-2 text-xs text-destructive">
          {validation.error.issues[0]?.message ?? 'Invalid URL'}
        </p>
      )}
    </div>
  );
}

function TextField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const wordCount = countWords(value);
  const overWordLimit = wordCount > 2000;
  return (
    <div>
      <label htmlFor="text-input" className="block text-sm font-medium text-foreground">
        Paste content to score
      </label>
      <textarea
        id="text-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={10}
        disabled={disabled}
        placeholder="Paste an article, blog post, or any text you want scored…"
        className="mt-2 block w-full rounded-md border border-border bg-card p-4 text-sm leading-relaxed shadow-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
      />
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className={overWordLimit ? 'text-destructive' : 'text-muted-foreground'}>
          {wordCount} / 2000 words
        </span>
        {overWordLimit && <span className="text-destructive">Trim your input below 2000 words.</span>}
      </div>
    </div>
  );
}

function ProcessingState() {
  return (
    <section className="mt-12 rounded-lg border border-border bg-card p-8 text-center">
      <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary" />
      <p className="mt-4 text-sm text-muted-foreground">
        Scoring your content. This usually takes 30–60 seconds.
      </p>
    </section>
  );
}

function friendlyError(code: string, message: string): { title: string; body: string } {
  switch (code) {
    case 'URL_FETCH_FAILED':
      return {
        title: "We couldn't reach that page",
        body:
          'The page returned an error, or the site is blocking us or asking for a login. Try opening it yourself and pasting the article text in the Text tab.',
      };
    case 'URL_BROWSER_FAILED':
      return {
        title: "We couldn't open that page",
        body:
          "Our browser failed to load the page, so there was nothing to score. That's a problem on our end, not with your link. Please open the page yourself, copy the article text, and paste it into the Text tab.",
      };
    case 'URL_NO_CONTENT':
      return {
        title: 'No article content found',
        body:
          "We loaded the page but couldn't identify a main article on it. Section fronts and homepages often look like this. If it is an article, paste its text into the Text tab.",
      };
    case 'REQUEST_REJECTED':
      return {
        title: 'That submission was rejected before it reached us',
        body: 'Something between your browser and our API turned it away, usually because the content was unusually large. Try trimming it, or split it into two submissions.',
      };
    case 'BAD_RESPONSE':
      return {
        title: 'We got a response we could not read',
        body: 'That is a problem on our end rather than with your content. Please try again in a moment.',
      };
    case 'TEXT_TOO_SHORT':
      return { title: 'Text too short', body: message };
    case 'WORD_COUNT_EXCEEDED':
      return { title: 'Text too long', body: message };
    case 'FILE_TOO_LARGE':
      return { title: 'File too large', body: 'Maximum 5 MB. Try a smaller file.' };
    case 'INVALID_CONTENT_TYPE':
      return {
        title: 'Unsupported file type',
        body: 'Use a PNG, JPEG, or PDF file.',
      };
    case 'FILE_VALIDATION_FAILED':
      return {
        title: "We couldn't read that file",
        body:
          'The model rejected the upload. The file may be corrupt, an unusual format variant, or too small to score. Try a different file.',
      };
    case 'UPLOAD_NOT_FOUND':
      return {
        title: 'Upload did not finish',
        body:
          "We never received the file, so there was nothing to score. This usually means the upload was interrupted. Pick the file again and retry — this attempt didn't count against your daily limit.",
      };
    case 'SCORE_PARSE_FAILED':
      return {
        title: "We couldn't read the score",
        body:
          'The model replied in an unexpected shape, so we discarded the result rather than show you a wrong one. Trying again usually works.',
      };
    case 'SCORE_REQUEST_INVALID':
      return {
        title: 'Something is wrong on our end',
        body:
          "The scoring request was rejected, which means this is our bug and not your content. Retrying won't help until we fix it.",
      };
    default:
      return { title: 'Analysis failed', body: message };
  }
}

/**
 * Inline error under the submit button, for failures that happen during the
 * submit calls themselves rather than inside the workflow.
 *
 * Requirement 5 made this path matter more: the file flow now makes three
 * calls (upload-url, PUT, submit), so a code like UPLOAD_NOT_FOUND surfaces
 * here and not through FailedState. Both surfaces share friendlyError so a
 * given code reads the same wherever the user meets it.
 */
function SubmitError({ error }: { error: unknown }) {
  let text: string;
  if (error instanceof ApiError && error.code) {
    const friendly = friendlyError(error.code, error.message);
    text = `${friendly.title}. ${friendly.body}`;
  } else {
    text = (error as Error | null)?.message ?? 'Something went wrong.';
  }
  return <p className="mt-3 text-sm text-destructive">{text}</p>;
}

function FailedState({ code, message }: { code: string; message: string }) {  const friendly = friendlyError(code, message);
  return (
    <section className="mt-12 rounded-lg border border-destructive/50 bg-destructive/5 p-6">
      <h2 className="font-semibold text-destructive">{friendly.title}</h2>
      <p className="mt-1 text-sm text-foreground">{friendly.body}</p>
      <p className="mt-3 font-mono text-xs text-muted-foreground">{code}</p>
    </section>
  );
}

